import React from 'react';
import { z } from 'zod';
import classNames from 'classnames';
import { BLOCKS } from '@contentful/rich-text-types';
import { ListItemType } from '$quantum/atoms/ListItem';
import { ChatConversation } from '$quantum/molecules/ChatConversation';
import type { ContentComponent } from '$cms/ContentComponent';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { CmsImageSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { SmallLabel } from '$quantum/atoms/SmallLabel';
import { VrmLookupModal } from '$quantum/molecules/VrmLookupModal';
import { extendCmsText } from '$cms/index';
import { Row, Col } from '@/components/atoms/Grid';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { getShortFrequencyQuestionData } from './utils/getShortFrequencyQuestionData';
import { getShortFrequencyAnswerData } from './utils/getShortFrequencyAnswerData';
import { replaceHeadings } from './utils';

import styles from './index.module.scss';

const ExpertInsightsSchema = {
  translatedEntities: z.object({
    customerDisplayName: CmsShortTextSchema,
    customerImage: CmsImageSchema,
    fixterExpertDisplayName: CmsShortTextSchema.optional(),
    fixterExpertImage: CmsImageSchema,
    cta: CmsShortTextSchema,
    cndAnywhere: CmsShortTextSchema,
    title: CmsShortTextSchema.optional(),
  }),
};

export type ExpertInsightsTranslatedEntities = SchemaType<
  (typeof ExpertInsightsSchema)['translatedEntities']
>;

const renderOptions = {
  bullets: ListItemType.Bullet,
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_text: unknown, children: React.ReactNode) => <p>{children}</p>,
  },
};

export const ExpertInsights: ContentComponent<ExpertInsightsTranslatedEntities, undefined> =
  function ExpertInsights({ translatedEntities, className, ...props }): JSX.Element {
    const cmsPageContext = useCmsPageContext();
    const pageProductCollection = cmsPageContext?.pageRoutes?.[0]?.collectionItems?.product;
    const {
      location: { pathname },
    } = useAppContext<AppContextGlobal>();

    const { cndAnywhere } = translatedEntities;

    /**
     * @NOTE use collection title or else
     */
    const title = translatedEntities?.title?.value || pageProductCollection?.name;
    const { shortDescription, shortSymptoms, shortItemsReplaced, shortFrequency } =
      pageProductCollection || {};

    if (shortDescription) replaceHeadings(shortDescription.content);
    if (shortSymptoms) replaceHeadings(shortSymptoms.content);
    if (shortItemsReplaced) replaceHeadings(shortItemsReplaced.content);

    return (
      <ContentComponentSection className={classNames(ExpertInsights.displayName, className)} {...props}>
        <Row>
          <Col>
            {title && (
              <HeadingLevels className={styles.heading} semanticLevel={2} styleLevel={2}>
                {extendCmsText(title, {
                  product: pageProductCollection?.name || '',
                })}
              </HeadingLevels>
            )}
          </Col>
        </Row>
        <Row>
          <Col className={styles.sections} medium={(shortFrequency && 6) || 12}>
            {shortDescription && renderRichTextContent(shortDescription, renderOptions)}

            {shortSymptoms && <>{renderRichTextContent(shortSymptoms, renderOptions)}</>}

            {pathname && pathname.includes('mot-and-service') && (
              <>
                <h3 className="h6">How much do I save with an MOT and Service bundle?</h3>
                <p>By purchasing an MOT and Service bundle you will save 50% of the price of the MOT.</p>
              </>
            )}

            {shortItemsReplaced && <>{renderRichTextContent(shortItemsReplaced, renderOptions)}</>}
          </Col>
          {shortFrequency && (
            <Col
              className={styles.sections}
              medium={(shortFrequency && 5) || 0}
              mediumStart={(shortFrequency && 8) || false}
            >
              <ChatConversation
                className={styles.chat}
                sender={{
                  displayName: translatedEntities.customerDisplayName.value,
                  avatarImageSrc: translatedEntities.customerImage.url,
                }}
                replier={{
                  displayName: translatedEntities.fixterExpertDisplayName?.value,
                  avatarImageSrc: translatedEntities.fixterExpertImage.url,
                }}
                conversation={{
                  messages: [
                    {
                      from: 'sender',
                      message: renderRichTextContent(getShortFrequencyQuestionData(shortFrequency)),
                    },
                    {
                      from: 'replier',
                      message: (
                        <>
                          {renderRichTextContent(getShortFrequencyAnswerData(shortFrequency))}
                          <div className="button-container">
                            <VrmLookupModal id="VrmLookupModalExpertInsights" />
                            <div className="c-d-details">
                              <SmallLabel>{cndAnywhere.value}</SmallLabel>
                            </div>
                          </div>
                        </>
                      ),
                    },
                  ],
                }}
              />
            </Col>
          )}
        </Row>
      </ContentComponentSection>
    );
  };

ExpertInsights.displayName = 'ExpertInsights';
ExpertInsights.schema = ExpertInsightsSchema;
